import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, formatCOP } from '../services/api';
import useInvalidate from '../hooks/useInvalidate';

const CAMPO_LABEL = {
  precio: 'Precio',
  precio_app: 'Precio app',
  precio_web: 'Precio web',
  cantidad: 'Cantidad',
  en_stock: 'Disponible',
  nombre: 'Nombre',
  activo: 'Activo',
};

const ORIGEN_LABEL = {
  inventario_txt: 'Archivo TXT',
  inventario_remoto: 'Actualización automática',
  admin: 'Admin manual',
  sistema: 'Sistema',
};

export default function InventarioPage({ pin, onToast }) {
  const fileRef = useRef(null);
  const [tab, setTab] = useState('importar');
  const [importando, setImportando] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [ultimoImport, setUltimoImport] = useState(null);
  const [importaciones, setImportaciones] = useState([]);
  const [cambios, setCambios] = useState([]);
  const [filtroCodigo, setFiltroCodigo] = useState('');

  const loadMeta = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [imp, cam] = await Promise.all([
        api.getInventarioImportaciones(),
        api.getInventarioCambios({ codigo: filtroCodigo }),
      ]);
      setImportaciones(imp.data || []);
      setCambios(cam.data || []);
      if (imp.data?.length) setUltimoImport(imp.data[0]);
    } catch (err) {
      setImportaciones([]);
      setCambios([]);
      setLoadError(err.message || 'No se pudo cargar el inventario');
      onToast?.(err.message || 'No se pudo cargar el inventario', 'error');
    } finally {
      setLoading(false);
    }
  }, [pin, filtroCodigo, onToast]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useInvalidate(['inventory', 'product'], loadMeta);

  function applyResult(stats, toastMsg) {
    setUltimoImport(stats);
    const n = Number(stats?.productos_actualizados) || 0;
    const miss = Number(stats?.no_encontrados) || 0;
    const created = Number(stats?.productos_creados) || 0;
    const fotos = Number(stats?.fotos_asignadas) || 0;
    const dupTxt = Number(stats?.duplicados_txt) || 0;
    const dupEv = Number(stats?.duplicados_evitados) || 0;
    onToast?.(
      toastMsg ||
        `Importado: ${n} actualizado${n === 1 ? '' : 's'}${
          created ? ` · ${created} creados` : ''
        }${fotos ? ` · ${fotos} fotos` : ''}${
          dupTxt ? ` · ${dupTxt} dup. en TXT omitidos` : ''
        }${dupEv ? ` · ${dupEv} dup. evitados` : ''}${
          miss ? ` · ${miss} no creados` : ''
        }`
    );
    loadMeta().then(() => {
      setUltimoImport((row) => ({ ...(row || {}), ...stats }));
    });
  }

  async function importarArchivo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportando(true);
    try {
      const content = await file.text();
      if (!String(content || '').trim()) {
        onToast?.('El archivo está vacío', 'error');
        return;
      }
      const res = await api.importarInventario({ content, archivo: file.name });
      applyResult(res.data);
    } catch (err) {
      onToast?.(err.message || 'No se pudo importar', 'error');
    } finally {
      setImportando(false);
      e.target.value = '';
    }
  }

  async function importarCarpeta() {
    setImportando(true);
    try {
      const res = await api.importarInventario({});
      applyResult(res.data, `Importado desde carpeta: ${res.data.productos_actualizados} actualizados`);
    } catch (err) {
      onToast?.(err.message || 'No se pudo leer el archivo de la carpeta', 'error');
    } finally {
      setImportando(false);
    }
  }

  return (
    <div className="panel inventario-page">
      <header className="panel-head">
        <div>
          <h2>Inventario</h2>
        </div>
      </header>

      <div className="tabs">
        <button
          type="button"
          className={tab === 'importar' ? 'active' : ''}
          onClick={() => setTab('importar')}
        >
          Importar
        </button>
        <button
          type="button"
          className={tab === 'historial' ? 'active' : ''}
          onClick={() => setTab('historial')}
        >
          Historial de cambios
        </button>
      </div>

      {loadError ? (
        <p className="error banner">
          {loadError}{' '}
          <button type="button" className="link-btn" onClick={loadMeta}>
            Reintentar
          </button>
        </p>
      ) : null}

      {tab === 'importar' ? (
        <div className="inventario-import">
          <div className="import-actions">
            <label className={`btn-primary file-btn${importando ? ' disabled' : ''}`}>
              {importando ? 'Importando…' : 'Subir archivo'}
              <input
                type="file"
                accept=".txt,.csv,text/plain"
                disabled={importando}
                onChange={importarArchivo}
                hidden
              />
            </label>
            <button
              type="button"
              className="btn-secondary"
              disabled={importando}
              onClick={importarCarpeta}
              title="Lee el archivo de inventario que ya está en el servidor"
            >
              Usar archivo del servidor
            </button>
            <button type="button" className="btn-ghost" disabled={loading} onClick={loadMeta}>
              Actualizar
            </button>
          </div>
          <p className="muted">
            Los productos que ya existen no se tocan (ni foto, ni nombre, ni categoría). Solo se actualiza
            stock/precio si cambió en el TXT. Si el mismo nombre viene con otro código (ej. 0909 y 20230909),
            se actualiza el producto que ya está y no se crea duplicado. Los que no existan se crean; la foto
            se asigna solo si ya hay un archivo con ese mismo código.
          </p>

          {ultimoImport ? (
            <div className="import-result">
              <strong>Última importación</strong>
              <span>Archivo: {ultimoImport.archivo || '—'}</span>
              <span>Filas: {ultimoImport.filas_leidas ?? '—'}</span>
              {ultimoImport.filas_procesadas != null ? (
                <span>Procesadas: {ultimoImport.filas_procesadas}</span>
              ) : null}
              <span>Actualizados: {ultimoImport.productos_actualizados ?? 0}</span>
              <span>Creados: {ultimoImport.productos_creados ?? 0}</span>
              {ultimoImport.duplicados_txt ? (
                <span>Dup. TXT omitidos: {ultimoImport.duplicados_txt}</span>
              ) : null}
              {ultimoImport.duplicados_evitados ? (
                <span>Dup. evitados: {ultimoImport.duplicados_evitados}</span>
              ) : null}
              <span>Fotos asignadas: {ultimoImport.fotos_asignadas ?? 0}</span>
              <span>Sin cambios: {ultimoImport.sin_cambios ?? 0}</span>
              <span>No encontrados: {ultimoImport.no_encontrados ?? 0}</span>
              {ultimoImport.created_at ? (
                <span>
                  Fecha: {new Date(ultimoImport.created_at).toLocaleString('es-CO')}
                </span>
              ) : null}
              {ultimoImport.creados_lista?.length ? (
                <em>
                  Creados (muestra): {ultimoImport.creados_lista.join(', ')}
                </em>
              ) : null}
              {ultimoImport.detalle ? <em>{ultimoImport.detalle}</em> : null}
              {ultimoImport.parse_errors?.length ? (
                <em className="import-errors">
                  Avisos de formato: {ultimoImport.parse_errors.slice(0, 5).join(' · ')}
                </em>
              ) : null}
            </div>
          ) : null}

          {loading && !importaciones.length ? (
            <p className="muted">Cargando historial…</p>
          ) : null}

          {importaciones.length > 0 ? (
            <>
              <h3 className="section-sub">Importaciones recientes</h3>
              <div className="table-wrap">
                <table className="responsive-cards">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Archivo</th>
                      <th>Actualizados</th>
                      <th>Sin cambio</th>
                      <th>No encontrados</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importaciones.slice(0, 15).map((i) => (
                      <tr key={i.id}>
                        <td data-label="Fecha">{new Date(i.created_at).toLocaleString('es-CO')}</td>
                        <td data-label="Archivo">{i.archivo}</td>
                        <td data-label="Actualizados">{i.productos_actualizados}</td>
                        <td data-label="Sin cambio">{i.sin_cambios}</td>
                        <td data-label="No encontrados">{i.no_encontrados}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : !loading ? (
            <p className="muted">Aún no hay importaciones. Sube el primer TXT.</p>
          ) : null}
        </div>
      ) : (
        <div className="inventario-historial">
          <div className="toolbar">
            <input
              type="search"
              placeholder="Filtrar por código..."
              value={filtroCodigo}
              onChange={(e) => setFiltroCodigo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadMeta()}
            />
            <button type="button" onClick={loadMeta}>
              Buscar
            </button>
          </div>

          {!cambios.length ? (
            <p className="muted">Sin cambios registrados aún.</p>
          ) : (
            <div className="table-wrap">
              <table className="responsive-cards">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Código</th>
                    <th>Producto</th>
                    <th>Campo</th>
                    <th>Antes</th>
                    <th>Después</th>
                    <th>Origen</th>
                  </tr>
                </thead>
                <tbody>
                  {cambios.map((c) => (
                    <tr key={c.id}>
                      <td data-label="Fecha">{new Date(c.created_at).toLocaleString('es-CO')}</td>
                      <td className="mono" data-label="Código">{c.codigo}</td>
                      <td data-label="Producto">{c.producto_nombre || '—'}</td>
                      <td data-label="Campo">{CAMPO_LABEL[c.campo] || c.campo}</td>
                      <td data-label="Antes">
                        {c.campo === 'precio' ? formatCOP(c.valor_anterior) : c.valor_anterior ?? '—'}
                      </td>
                      <td data-label="Después">
                        {c.campo === 'precio' ? formatCOP(c.valor_nuevo) : c.valor_nuevo ?? '—'}
                      </td>
                      <td data-label="Origen">{ORIGEN_LABEL[c.origen] || c.origen}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
